import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import url from '../url';
import { buildStudentClassInvitePath, POST_LOGIN_REDIRECT_KEY } from '../utils/studentClassRoutes';

export default function JoinStudentClass() {
  const { inviteCode } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useContext(AuthContext);
  const { t } = useTranslation('homepage');
  const [invitation, setInvitation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [joined, setJoined] = useState(false);
  const [alreadyMember, setAlreadyMember] = useState(false);
  const [loginRequired, setLoginRequired] = useState(false);
  const joiningInvite = useRef(null);

  useEffect(() => {
    const pendingPath = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
    if (pendingPath === buildStudentClassInvitePath(inviteCode)) {
      sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
    }
  }, [inviteCode]);

  useEffect(() => {
    axios.get(`${url.backendHost}api/student-classes/invites/${encodeURIComponent(inviteCode)}`)
      .then((response) => setInvitation(response.data))
      .catch(() => setError(t('studentManagement.invitationNotFound')))
      .finally(() => setLoading(false));
  }, [inviteCode, t]);

  useEffect(() => {
    if (!invitation || invitation.joinEnabled === false || !user || authLoading || joiningInvite.current === inviteCode) return undefined;
    joiningInvite.current = inviteCode;
    let active = true;
    setLoading(true);
    setError('');
    const token = localStorage.getItem('jwtToken');
    axios.post(
      `${url.backendHost}api/student-classes/invites/${encodeURIComponent(inviteCode)}/join`,
      {},
      {
        withCredentials: true,
        headers: token ? { Authorization: ['Bearer', token].join(' ') } : undefined
      }
    )
      .then((response) => {
        if (active) {
          setAlreadyMember(Boolean(response.data?.alreadyMember || response.data?.status === 'already_member'));
          setJoined(true);
        }
      })
      .catch((requestError) => {
        if (!active) return;
        if (requestError.response?.data?.code === 'CLASS_JOIN_DISABLED') {
          setError(t('studentManagement.joinDisabled'));
        } else if (requestError.response?.status === 401) {
          setLoginRequired(true);
          setError(t('studentManagement.loginToJoin'));
        } else {
          setError(requestError.response?.data?.message || t('studentManagement.joinFailed'));
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [authLoading, invitation, inviteCode, t, user]);

  const goToLogin = () => {
    sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, buildStudentClassInvitePath(inviteCode));
    navigate('/');
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 2, bgcolor: '#f5f7fa' }}>
      <Card sx={{ maxWidth: 480, width: '100%' }}>
        <CardContent sx={{ p: 4, textAlign: 'center' }}>
          {(loading || authLoading) && <CircularProgress />}
          {!loading && !authLoading && (
            <>
              <Typography variant="h5" fontWeight={700} gutterBottom>
                {alreadyMember
                  ? t('studentManagement.alreadyInClass')
                  : joined ? t('studentManagement.joinedTitle') : t('studentManagement.confirmJoinTitle')}
              </Typography>
              {invitation && (
                <>
                  <Typography variant="h6" color="primary" sx={{ mt: 2 }}>{invitation.className}</Typography>
                </>
              )}
              {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
              {invitation?.joinEnabled === false && (
                <Alert severity="warning" sx={{ mt: 2 }}>{t('studentManagement.classJoiningDisabledMessage')}</Alert>
              )}
              {!joined && invitation && user && !error && (
                <Typography sx={{ mt: 3 }}>{t('studentManagement.joiningClass')}</Typography>
              )}
              {!joined && invitation && (!user || loginRequired) && (
                <Button variant="contained" onClick={goToLogin} sx={{ mt: 3 }}>
                  {t('studentManagement.openLogin')}
                </Button>
              )}
              {joined && (
                <Button variant="contained" onClick={() => navigate('/main')} sx={{ mt: 3 }}>
                  {t('studentManagement.goToMain')}
                </Button>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
}
